/**
 * How wide the thread column is allowed to be, and what it is by default.
 *
 * The width used to be one expression in `Thread.tsx` — a clamped share of the
 * stage — and it is now a number the reader can drag. That turns one arithmetic
 * into three questions that have to agree: what the column is when nobody has
 * said, what it may never be narrower than, and what it may never be wider than
 * on the window it finds itself in. They live together here, with no React and
 * no DOM in them, because the third one is asked again on every window resize
 * and a stored width that pinned the canvas shut would otherwise be unreachable
 * from the surface that made it.
 *
 * Imports nothing.
 */

/**
 * The narrowest the column may be, and it is measured rather than chosen.
 *
 * The pane's own content already proves a floor: below the phone breakpoint this
 * same feed, the same selection strip and the same composer render inside a
 * bottom sheet that is `left: 8, right: 8` of the window, so at the 375px
 * viewport the app is written against they are living at 359. A desktop column
 * narrower than the phone's own sheet would be asking this content to do
 * something it has never had to do; one at 360 is asking for exactly what it
 * already does every day.
 *
 * The other half of the same fact is App's: the transport "fits at 375 and fits
 * here", said of the composer strip that rides this pane's foot.
 */
export const THREAD_MIN = 360

/**
 * What the canvas keeps, whatever the column would like.
 *
 * The field is the app. A column dragged until the graph is a stripe has taken
 * the thing the thread is a thread ABOUT, so the maximum is stated from the
 * canvas's side rather than the pane's: whatever is left after the sidebar and
 * this floor. 420 is the smallest field the run pod, the captions and a legible
 * node still share without overlapping — the width at which the pane regime's
 * bottom furniture (the dock's 88px floor plus the minimap's 132 + 8) still has
 * daylight between its two ends.
 */
export const THREAD_CANVAS_FLOOR = 420

/**
 * The column's live width, as a custom property on the document root.
 *
 * A DRAG MUST NOT GO THROUGH THE STORE. `setViewInset` bumps `rev`, which is the
 * signal that asks the canvas to re-follow the graph — an eased camera flight —
 * so writing the width there on every pointer sample would restart that flight
 * sixty times a second and re-render every `rev` consumer with it. And a React
 * state in the pane is barely better: this column holds the whole conversation,
 * the selection strip and the composer, so re-rendering it per sample is the
 * expensive half of the same mistake.
 *
 * So the pane's own box reads this property and nothing else does. The handle
 * writes it directly while a drag is live; App writes it from the committed
 * width, which is the value of record. The pane's material follows for free:
 * a ground, docked or on a phone, which keeps no copy of anything sized to the
 * box (D357, D358).
 */
export const THREAD_W_VAR = '--thread-w'

/**
 * The reopen tab's width, reserved from the field like the column itself.
 *
 * Thirteen pixels of glass, and it is measured into `viewInset` rather than left
 * to float over the graph: the camera's left inset is what keeps nodes and their
 * captions out from under a pane, and a tab that sat outside it would be a
 * control drawn on top of the one thing this app is for. Reserving it is one
 * more term in an expression that already has exactly this shape — and it is
 * cheaper than adding a rectangle to the caption layer's list, which would keep
 * captions off it and let a node slide under it anyway.
 */
export const THREAD_TAB_W = 13

/** One press of ← or → on the separator. Big enough to be worth a press, small
 *  enough that a reader can land on a width they meant; the same order as the
 *  8px grid this app measures everything else in, times three. */
export const THREAD_STEP = 24

/**
 * How much of a phone's height the thread sheet covers: half. The sheet's own
 * box reads it (`Thread.tsx`). The camera does not do this arithmetic: it
 * measures the sheet it is framing around (`canvas/fieldInset.ts`), so a change
 * here reaches the fit without a second number to keep in step.
 */
export const PHONE_SHEET_SHARE = 0.5

/**
 * The width nobody has asked for: 380, the owner's design for the column (D353).
 *
 * It was a share of the stage clamped to 400–480, because a fixed 480 beside the
 * old 280 sidebar left a 1280 stage's field narrower than the composer floating
 * over it. At 380 beside the 224 sidebar the field keeps more on every window
 * than that expression ever left it, so one number serves; a window too small
 * for the field's floor is still answered by the bounds, on every read. Wider
 * is the handle's to give.
 */
export const THREAD_DEFAULT = 380

/**
 * What the column may be on this window, with this much of it already spoken
 * for by the sidebar.
 *
 * `max` is never allowed below `min`, and that ordering is the whole of the
 * small-window case: on a narrow desktop the canvas floor cannot be paid at all,
 * and a `max` that went under `min` would make every clamp below answer with
 * something outside its own range. The pane takes its minimum there and the
 * field takes what is left — which is more than it got before this existed,
 * because the old expression never went below 400.
 */
export function threadBounds(stageW: number, sidebarInset: number): { min: number; max: number } {
  const room = Math.round(stageW - sidebarInset - THREAD_CANVAS_FLOOR)
  return { min: THREAD_MIN, max: Math.max(THREAD_MIN, room) }
}

/**
 * The column's width now: what was stored if anything was, the default if not,
 * held inside the bounds either way.
 *
 * CLAMPED ON EVERY READ rather than only when it is set, which is what makes a
 * stored width safe to keep. A column dragged wide on a 2560 monitor and
 * reopened on a laptop would otherwise leave the canvas a sliver with no way
 * back — the handle that would fix it being off the edge of the window it is
 * now on. Here the same stored number simply reads as the widest that window
 * allows, and the reader's setting is still there when they go back to the big
 * screen.
 */
export function threadWidthFrom(
  stageW: number,
  sidebarInset: number,
  stored: number | null | undefined,
): number {
  const { min, max } = threadBounds(stageW, sidebarInset)
  const want = typeof stored === 'number' && Number.isFinite(stored) ? stored : THREAD_DEFAULT
  return Math.min(max, Math.max(min, Math.round(want)))
}

/** One step of the separator's keyboard, held inside the same bounds. Exported
 *  so the handle and its tests do the arithmetic once. */
export function stepThreadWidth(
  current: number,
  by: number,
  stageW: number,
  sidebarInset: number,
): number {
  return threadWidthFrom(stageW, sidebarInset, current + by)
}
